import type { GenUIEvent } from '../core/events';
import { textDelta, componentEvent, doneEvent } from '../core/events';
import type { GenUIResponse } from '../core/parser';
import { processEventSource } from './sse';
import { processReadableStream } from './readableStream';

/**
 * Stream input types
 */
export type StreamInput =
    | EventSource
    | ReadableStream<Uint8Array>
    | Response;

/**
 * Check if input is an EventSource
 */
function isEventSource(input: unknown): input is EventSource {
    return typeof EventSource !== 'undefined' && input instanceof EventSource;
}

/**
 * Check if input is a ReadableStream
 */
function isReadableStream(input: unknown): input is ReadableStream<Uint8Array> {
    return typeof ReadableStream !== 'undefined' && input instanceof ReadableStream;
}

/**
 * Check if input is a Response (from fetch)
 */
function isResponse(input: unknown): input is Response {
    return typeof Response !== 'undefined' && input instanceof Response;
}

/**
 * Normalize any stream input to a GenUIEvent async iterator
 */
export async function* normalizeStream(
    input: StreamInput
): AsyncGenerator<GenUIEvent> {
    if (isEventSource(input)) {
        yield* processEventSource(input);
    } else if (isReadableStream(input)) {
        yield* processReadableStream(input);
    } else if (isResponse(input)) {
        if (!input.body) {
            yield doneEvent();
            return;
        }
        yield* processReadableStream(input.body);
    } else {
        throw new Error('Unsupported stream input type');
    }
}

/**
 * Convert a static response to GenUIEvents
 */
export function* normalizeResponse(response: GenUIResponse): Generator<GenUIEvent> {
    // Yield text as single delta
    if (response.text) {
        yield textDelta(response.text);
    }

    // Yield each component
    for (const component of response.components) {
        yield componentEvent(component);
    }

    yield doneEvent();
}
